import { Fragment, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Database } from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { EvidenceToolMessage } from '../ScreenataChatToolMessages'
import {
  BagDelegate,
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolDetailOverlayLink,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const dbIcon = <Database className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />

function statusTone(value: unknown) {
  if (typeof value !== 'string') return 'default'
  switch (value.toLowerCase()) {
    case 'fail':
    case 'error':
    case 'expired':
      return 'critical'
    case 'warn':
    case 'stale':
      return 'medium'
    case 'pass':
    case 'fresh':
    case 'complete':
    case 'completed':
      return 'success'
    default:
      return normalizeTone(value)
  }
}

function readCountEntries(value: unknown): Array<[string, number]> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return []
  return Object.entries(value as Record<string, unknown>)
    .map(
      ([key, count]) => [key, typeof count === 'number' ? count : 0] as const
    )
    .filter(([, count]) => count > 0)
}

function readControlEntries(
  value: unknown
): Array<[string, Record<string, number>]> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return []
  return Object.entries(value as Record<string, unknown>)
    .map(
      ([key, summary]) =>
        [
          key,
          summary && typeof summary === 'object' && !Array.isArray(summary)
            ? (summary as Record<string, number>)
            : {},
        ] as const
    )
    .filter(([, summary]) => typeof summary.total === 'number')
}

function readRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function readTableColumns(value: unknown): string[] | undefined {
  const table = readRecord(value)
  if (!table || !Array.isArray(table.columns)) return undefined
  const columns = table.columns
    .map((column) => {
      if (typeof column === 'string') return column.trim()
      if (!column || typeof column !== 'object' || Array.isArray(column)) {
        return ''
      }
      const label = (column as Record<string, unknown>).label
      return typeof label === 'string' ? label.trim() : ''
    })
    .filter((label) => label.length > 0)

  return columns.length > 0 ? columns : undefined
}

function readTableTitle(value: unknown): string | undefined {
  const table = readRecord(value)
  if (typeof table?.title !== 'string') return undefined
  const title = table.title.trim()
  return title.length > 0 ? title : undefined
}

function formatSummary(value: unknown): string | null {
  const summary = readRecord(value)
  if (!summary) return null
  return Object.entries(summary)
    .filter(([, count]) => typeof count === 'number' && count > 0)
    .map(([key, count]) => `${key}: ${count}`)
    .join(', ')
}

function useInvalidateEvidenceSubmissionQueries(
  testId: string | null,
  isComplete: boolean,
  isError: boolean
) {
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!isComplete || isError || !testId) return
    queryClient.invalidateQueries({
      queryKey: trpc.guidedTemplate.getStatusById.queryKey({ id: testId }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.guidedTemplate.listApiTestRuns.queryKey({
        statusId: testId,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.guidedTemplate.getLatestApiTestRun.queryKey({
        statusId: testId,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.evidenceDocLink.list.queryKey({ statusId: testId }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.evidenceFile.list.queryKey({ statusId: testId }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.compliance.getRecommendedEvidence.queryKey({
        statusId: testId,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.evidencePack.listForStatus.queryKey({ statusId: testId }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.evidenceFile.listTicketAttachments.queryKey({
        statusId: testId,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.audit.getActivityFeed.infiniteQueryKey({
        targetType: 'control_evidence_status',
        targetId: testId,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.compliance.getControlsTree.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.guidedTemplate.getMyTasks.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.guidedTemplate.getReviewQueue.queryKey(),
    })
  }, [isComplete, isError, testId, queryClient])
}

export function EvidenceListEvidenceToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const total =
    typeof state.result?.totalDocuments === 'number'
      ? state.result.totalDocuments
      : typeof state.result?.total === 'number'
        ? state.result.total
        : null
  const freshnessItems = readCountEntries(state.result?.byFreshness).map(
    ([stateName, count]) => ({
      id: stateName,
      title: stateName,
      subtitle: 'freshness',
      meta: [`${count} item${count === 1 ? '' : 's'}`],
      tone: normalizeTone(stateName),
    })
  )
  const sourceItems = readCountEntries(state.result?.bySource).map(
    ([sourceType, count]) => ({
      id: sourceType,
      title: sourceType,
      subtitle: 'source',
      meta: [`${count} item${count === 1 ? '' : 's'}`],
    })
  )
  const controlItems = readControlEntries(state.result?.byControl).map(
    ([controlRef, summary]) => ({
      id: controlRef,
      title: pickString(state.result?.controlCodes?.[controlRef]) ?? controlRef,
      subtitle: 'control',
      meta: [
        `${summary.total ?? 0} item${summary.total === 1 ? '' : 's'}`,
        summary.fresh ? `${summary.fresh} fresh` : null,
        summary.stale || summary.expired
          ? `${(summary.stale ?? 0) + (summary.expired ?? 0)} stale`
          : null,
      ],
      tone:
        (summary.expired ?? 0) > 0 || (summary.stale ?? 0) > 0
          ? ('medium' as const)
          : ('success' as const),
    })
  )
  const previewItems =
    controlItems.length > 0
      ? controlItems
      : freshnessItems.length > 0
        ? freshnessItems
        : sourceItems
  const previewColumns =
    readTableColumns(state.result?.table) ??
    (controlItems.length > 0
      ? ['Control', 'Type', 'Evidence', 'Fresh', 'Stale']
      : freshnessItems.length > 0
        ? ['Freshness', 'Type', 'Evidence']
        : sourceItems.length > 0
          ? ['Source', 'Type', 'Evidence']
          : undefined)
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading evidence...'
        completedLabel={
          total !== null
            ? `${plural(total, 'item')} in vault`
            : 'Evidence loaded'
        }
        completedIcon={dbIcon}
      />
      {state.isComplete && !state.isError && !isToolError(state) ? (
        <ToolListPreview
          columns={previewColumns}
          emptyLabel='No evidence found'
          items={previewItems}
          title={readTableTitle(state.result?.table)}
          moreLabel={
            controlItems.length > 0 && freshnessItems.length > 0
              ? `${freshnessItems.length} freshness groups, ${sourceItems.length} sources`
              : undefined
          }
        />
      ) : null}
    </>
  )
}

export function EvidenceGetEvidenceToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const latestPerCheck: Array<Record<string, unknown>> = Array.isArray(
    state.result?.latestPerCheck
  )
    ? state.result.latestPerCheck
    : []
  const freshness = readRecord(state.result?.evidenceFreshness)
  const scanRun = readRecord(state.result?.scanRun)
  const provider = pickString(
    state.args?.provider,
    state.result?.autoCollectedProvider,
    latestPerCheck[0]?.provider
  )
  const scope = [state.args?.controlRef, provider, state.args?.checkId]
    .filter(Boolean)
    .join(' / ')
  const detail =
    state.isComplete && state.result?.autoCollected
      ? [scope, `refreshed ${state.result.autoCollectedProvider ?? ''}`]
          .filter(Boolean)
          .join(' · ')
      : state.isComplete && state.result?.autoCollectSkipped
        ? [scope, state.result.autoCollectReason].filter(Boolean).join(' · ')
        : scope
  const toolCallId = getToolCallId(part, 'evidence-detail')
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Retrieving evidence...'
        completedLabel='Evidence loaded'
        completedDetail={detail}
        completedIcon={dbIcon}
      />
      {state.isComplete && !state.isError && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No check evidence found'
          items={latestPerCheck.map((item, index) => {
            const checkId = pickString(item.checkId) ?? 'Unknown check'
            const minimumFix = Array.isArray(item.minimumFix)
              ? item.minimumFix.filter(
                  (entry): entry is string =>
                    typeof entry === 'string' && entry.trim().length > 0
                )
              : []
            return {
              id: pickString(item.sourceId, item.checkId),
              title: checkId,
              subtitle: pickString(item.controlRef, item.provider),
              meta: [
                item.status,
                item.source,
                pickString(item.freshnessState, item.collectedAt),
              ],
              tone: statusTone(item.status),
              detailOverlay: {
                id: `${toolCallId}-${pickString(item.sourceId, item.checkId) ?? index}`,
                payload: {
                  entityType: 'evidence',
                  toolName: 'evidenceGetEvidence',
                  title: checkId,
                  subtitle: pickString(item.controlRef, item.provider),
                  description: pickString(item.finding),
                  fields: [
                    {
                      label: 'Status',
                      value: formatToolValue(item.status),
                      tone: statusTone(item.status),
                    },
                    {
                      label: 'Provider',
                      value: formatToolValue(item.provider ?? provider),
                    },
                    {
                      label: 'Control',
                      value: formatToolValue(item.controlRef),
                    },
                    {
                      label: 'Source',
                      value: formatToolValue(item.source),
                    },
                    {
                      label: 'Freshness',
                      value: formatToolValue(item.freshnessState),
                      tone: statusTone(item.freshnessState),
                    },
                    {
                      label: 'Collected',
                      value: formatToolValue(item.collectedAt),
                    },
                    {
                      label: 'Run',
                      value: formatToolValue(item.runId),
                    },
                    {
                      label: 'Confidence',
                      value: formatToolValue(item.confidence),
                    },
                  ],
                  sections: [
                    {
                      title: 'Minimum Fix',
                      content:
                        minimumFix.length > 0
                          ? minimumFix.map((entry) => `- ${entry}`).join('\n')
                          : null,
                    },
                    {
                      title: 'Query Context',
                      content: [
                        formatSummary(state.result?.latestPerCheckSummary),
                        freshness?.state
                          ? `Freshness: ${freshness.state}`
                          : null,
                        scanRun?.status ? `Scan run: ${scanRun.status}` : null,
                      ]
                        .filter(Boolean)
                        .join('\n'),
                    },
                  ],
                },
              },
            }
          })}
        />
      ) : null}
    </>
  )
}

// Fulfill* actions trigger queryClient invalidation in the bag → keep bag.
export function EvidenceFulfillWithPolicyToolMessage(props: ToolPartProps) {
  return (
    <BagDelegate
      Component={EvidenceToolMessage}
      action='fulfillWithPolicy'
      {...props}
    />
  )
}

/**
 * Card linking to a real test or document entity (opens the system-detail
 * overlay on click). `payload` carries only the surface title/subtitle —
 * `systemDetail` deep-links to the live entity by id.
 */
function EntityCard({
  part,
  overlayKey,
  entityType,
  entityId,
  title,
  subtitle,
  toolName,
  label,
}: {
  part: any
  overlayKey: string
  entityType: 'test' | 'document'
  entityId: string
  title: string
  subtitle?: string | null
  toolName: string
  label: string
}) {
  return (
    <ToolDetailOverlayLink
      overlayId={getToolCallId(part, overlayKey)}
      systemDetail={{ entityType, entityId, workspaceId: '' }}
      payload={{
        entityType,
        toolName,
        title,
        subtitle: subtitle ?? null,
        fields: [],
      }}
      label={label}
    />
  )
}

export function EvidenceFulfillWithDocumentToolMessage(props: ToolPartProps) {
  const state = readToolState(props.part)
  const testId = pickString(state.result?.testId, state.args?.testId)
  const taskCode = pickString(state.result?.taskCode, state.args?.taskCode)
  const documentId = pickString(state.result?.documentId, state.args?.documentId)
  const documentTitle = pickString(state.result?.documentTitle)
  const showCards =
    state.isComplete &&
    !state.isError &&
    !isToolError(state) &&
    (!!testId || !!documentId)
  return (
    <>
      {/* Bag keeps the status line + the fulfill query-invalidation effect. */}
      <BagDelegate
        Component={EvidenceToolMessage}
        action='fulfillWithDocument'
        {...props}
      />
      {showCards ? (
        <>
          {documentId ? (
            <EntityCard
              part={props.part}
              overlayKey={`fulfill-doc-${documentId}`}
              entityType='document'
              entityId={documentId}
              title={documentTitle ?? 'Document'}
              subtitle='Evidence'
              toolName='evidenceFulfillWithDocument'
              label='Open'
            />
          ) : null}
          {testId ? (
            <EntityCard
              part={props.part}
              overlayKey={`fulfill-test-${testId}`}
              entityType='test'
              entityId={testId}
              title={taskCode ?? testId}
              toolName='evidenceFulfillWithDocument'
              label='Open test'
            />
          ) : null}
        </>
      ) : null}
    </>
  )
}

/**
 * Renders the result of saving a chat-attached file to the Documents vault:
 * a document card per saved file plus entity cards for classifier-suggested
 * tests (matching the fulfill-with-document card stack). Failures fall through
 * to `ToolStatusLine`, which surfaces the structured error message behind the
 * expandable label.
 */
export function EvidenceSaveUploadedFileToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const saved: Array<Record<string, unknown>> = Array.isArray(
    state.result?.saved
  )
    ? state.result.saved
    : []
  const savedDocs = saved.filter((doc) => !!pickString(doc?.documentId))
  const filenames = saved
    .map((doc) => pickString(doc?.filename))
    .filter((name): name is string => !!name)
  const showResults =
    state.isComplete && !state.isError && !isToolError(state) && savedDocs.length > 0
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Saving file to Documents...'
        completedLabel={
          savedDocs.length > 1
            ? `Saved ${plural(savedDocs.length, 'file')} to Documents`
            : 'Saved to Documents'
        }
        completedDetail={filenames.join(', ') || null}
        completedIcon={dbIcon}
      />
      {showResults
        ? savedDocs.map((doc, index) => {
            const documentId = pickString(doc.documentId)!
            const filename = pickString(doc.filename) ?? 'Document'
            const alreadySaved = doc.alreadySaved === true
            const suggested: Array<Record<string, unknown>> = Array.isArray(
              doc.suggestedTests
            )
              ? doc.suggestedTests
              : []
            return (
              <Fragment key={`${documentId}-${index}`}>
                <EntityCard
                  part={part}
                  overlayKey={`saved-doc-${documentId}-${index}`}
                  entityType='document'
                  entityId={documentId}
                  title={filename}
                  subtitle={
                    suggested.length > 0
                      ? 'Evidence'
                      : alreadySaved
                        ? 'Already in Documents'
                        : 'Saved to Documents'
                  }
                  toolName='evidenceSaveUploadedFile'
                  label='Open'
                />
                {suggested.map((test, testIndex) => {
                  const testId = pickString(test?.testId)
                  if (!testId) return null
                  const taskCode = pickString(test?.taskCode)
                  return (
                    <EntityCard
                      key={`${testId}-${testIndex}`}
                      part={part}
                      overlayKey={`saved-test-${testId}-${index}-${testIndex}`}
                      entityType='test'
                      entityId={testId}
                      title={taskCode ?? pickString(test?.title) ?? testId}
                      toolName='evidenceSaveUploadedFile'
                      label='Open test'
                    />
                  )
                })}
              </Fragment>
            )
          })
        : null}
    </>
  )
}

export function EvidenceFulfillWithReportToolMessage(props: ToolPartProps) {
  return (
    <BagDelegate
      Component={EvidenceToolMessage}
      action='fulfillWithReport'
      {...props}
    />
  )
}

export function EvidenceFulfillTestToolMessage(props: ToolPartProps) {
  return (
    <BagDelegate
      Component={EvidenceToolMessage}
      action='fulfillTest'
      {...props}
    />
  )
}

export function EvidenceSubmitForReviewToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const testId =
    pickString(state.result?.testId) ?? pickString(state.args?.testId)
  useInvalidateEvidenceSubmissionQueries(
    testId,
    state.isComplete,
    state.isError || isToolError(state)
  )
  const revision =
    typeof state.result?.revisionNumber === 'number'
      ? `revision ${state.result.revisionNumber}`
      : null
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Submitting evidence for review...'
      completedLabel='Evidence submitted for review'
      completedDetail={pickString(
        revision,
        state.result?.submissionId,
        state.args?.taskCode,
        testId
      )}
      completedIcon={dbIcon}
    />
  )
}

export function EvidenceWithdrawSubmissionToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const testId =
    pickString(state.result?.testId) ?? pickString(state.args?.testId)
  useInvalidateEvidenceSubmissionQueries(
    testId,
    state.isComplete,
    state.isError || isToolError(state)
  )
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Withdrawing evidence submission...'
      completedLabel='Evidence submission withdrawn'
      completedDetail={pickString(
        state.result?.submissionId,
        state.args?.submissionId,
        state.args?.taskCode,
        testId
      )}
      completedIcon={dbIcon}
    />
  )
}

export function EvidenceReviewSubmissionToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Reviewing submission...'
      completedLabel='Submission reviewed'
      completedDetail={pickString(
        state.result?.decision,
        state.args?.submissionId
      )}
      completedIcon={dbIcon}
    />
  )
}

export function EvidenceApproveWithStatusSyncToolMessage({
  part,
}: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Approving evidence...'
      completedLabel='Evidence approved'
      completedDetail={pickString(state.args?.testId, state.args?.submissionId)}
      completedIcon={dbIcon}
    />
  )
}
